import React, { useState, useEffect } from 'react';
import api from '../api';
import { decodeEntities } from '../utils/decodeEntities';

export default function ProjectDetailModal({ project, tasks = [], onClose, onEdit, onOpenNotes }) {
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    loadFiles();
    loadNotes();
  }, [project.id]);

  const loadFiles = async () => {
    try {
      const ids = JSON.parse(project.meta?.gpf_file_ids || '[]');
      if (!ids.length) { setFiles([]); return; }
      const results = await Promise.all(ids.map(id => api.get(`/wp/v2/media/${id}`).catch(() => null)));
      setFiles(results.filter(Boolean).map(r => r.data));
    } catch { setFiles([]); }
  };

  const loadNotes = async () => {
    try {
      const res = await api.get(`/wp/v2/notes?project_id=${project.id}&per_page=3&orderby=date&order=desc`);
      setNotes(res.data || []);
    } catch { setNotes([]); }
  };

  const status   = project.meta?.gpf_status   || 'active';
  const priority = project.meta?.gpf_priority || 'medium';
  const progress = project.meta?.gpf_progress || 0;
  const endDate  = project.meta?.gpf_end_date  || '';
  const dept     = project.meta?.gpf_department || '';
  const desc     = decodeEntities(project.content?.rendered) || project.meta?.gpf_description || '';

  const statusColors = {
    active:       { bg: '#eff6ff', text: '#2563eb' },
    completed:    { bg: '#f0fdf4', text: '#16a34a' },
    not_started:  { bg: '#f8fafc', text: '#64748b' },
    archived:     { bg: '#f8fafc', text: '#94a3b8' },
  };
  const priorityColors = {
    high:   { bg: '#fef2f2', text: '#dc2626' },
    medium: { bg: '#fffbeb', text: '#d97706' },
    low:    { bg: '#f0fdf4', text: '#16a34a' },
  };
  const sc = statusColors[status] || statusColors.active;
  const pc = priorityColors[priority] || priorityColors.medium;

  const fileIcon = (mime = '') => {
    if (mime.startsWith('image/')) return '🖼';
    if (mime.includes('pdf'))      return '📄';
    if (mime.includes('word'))     return '📝';
    return '📎';
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={styles.badgeRow}>
              <span style={{ ...styles.badge, background: sc.bg, color: sc.text }}>
                {status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
              <span style={{ ...styles.badge, background: pc.bg, color: pc.text }}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)} priority
              </span>
              {dept && <span style={styles.deptText}>{dept}</span>}
            </div>
            <h2 style={styles.title}>{decodeEntities(project.title?.rendered) || 'Untitled Project'}</h2>
            {project.meta?.gpf_started_by && (
              <p style={styles.createdBy}>
                🚀 Created by {project.meta.gpf_started_by}
                {project.meta?.gpf_started_date && ` on ${project.meta.gpf_started_date}`}
              </p>
            )}
          </div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <div style={styles.body}>
          {/* Description */}
          {desc && (
            <p style={styles.desc} dangerouslySetInnerHTML={{ __html: desc }} />
          )}
          {!desc && <p style={styles.descEmpty}>No description provided.</p>}

          {/* Stats grid */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Due date</div>
              <div style={styles.statValue}>{endDate || '—'}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Progress</div>
              <div style={styles.statValue}>{progress}%</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Tasks</div>
              <div style={styles.statValue}>{tasks.length} total</div>
            </div>
          </div>

          {/* Team */}
          <div style={styles.section}>
            <p style={styles.sectionLabel}>Team</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(() => {
                const ids = JSON.parse(project.meta?.gpf_assigned_users || '[]');
                if (!ids.length) return <span style={{ fontSize: 13, color: '#94a3b8' }}>No one assigned</span>;
                return (
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', background: '#f1f5f9', padding: '5px 12px', borderRadius: 99 }}>
                    👤 {ids.length} {ids.length === 1 ? 'person' : 'people'} assigned
                  </span>
                );
              })()}
            </div>
          </div>

          {/* Tasks */}
          {tasks.length > 0 && (
            <div style={styles.section}>
              <p style={styles.sectionLabel}>Tasks ({tasks.length})</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {tasks.slice(0, 6).map(task => {
                  const isDone = (task.meta?.gpf_status || '') === 'completed' || task.meta?.gpf_progress === 100;
                  let fileCount = 0;
                  try { fileCount = JSON.parse(task.meta?.gpf_file_ids || '[]').length; } catch {}
                  return (
                    <div key={task.id} style={styles.taskRow}>
                      <span style={{ color: isDone ? '#16a34a' : '#94a3b8' }}>{isDone ? '✓' : '○'}</span>
                      <span style={{ flex: 1, fontSize: 13, textDecoration: isDone ? 'line-through' : 'none', color: isDone ? '#94a3b8' : '#1e293b' }}>
                        {decodeEntities(task.title?.rendered) || 'Untitled'}
                      </span>
                      {fileCount > 0 && <span style={styles.miniAttach}>📎 {fileCount}</span>}
                    </div>
                  );
                })}
                {tasks.length > 6 && (
                  <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 0' }}>+{tasks.length - 6} more</p>
                )}
              </div>
            </div>
          )}

          {/* Files */}
          {files.length > 0 && (
            <div style={styles.section}>
              <p style={styles.sectionLabel}>Files</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {files.map(f => (
                  <a key={f.id} href={f.source_url} target="_blank" rel="noreferrer" style={styles.fileRow}>
                    <span>{fileIcon(f.mime_type)}</span>
                    <span style={{ fontSize: 13, color: '#1e293b' }}>{decodeEntities(f.title?.rendered) || 'File'}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Recent notes */}
          {notes.length > 0 && (
            <div style={styles.section}>
              <p style={styles.sectionLabel}>Recent notes</p>
              {notes.map(note => (
                <div key={note.id} style={styles.noteItem}>
                  <p style={styles.noteText} dangerouslySetInnerHTML={{ __html: decodeEntities(note.content?.rendered) || '' }} />
                  <span style={styles.noteMeta}>{note.meta?.gpf_created_by_name || 'Team'} · {note.date ? new Date(note.date).toLocaleDateString() : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={styles.footer}>
          <button style={styles.editBtn} onClick={() => { onClose(); onEdit(project); }}>Edit Project</button>
          <button style={styles.notesBtn} onClick={() => { onClose(); onOpenNotes(project); }}>Open Notes</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal:       { background: 'white', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' },
  header:      { padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  badgeRow:    { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' },
  badge:       { fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99 },
  deptText:    { fontSize: 12, color: '#94a3b8' },
  title:       { fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0, lineHeight: 1.3 },
  createdBy:   { fontSize: 12, color: '#94a3b8', margin: '6px 0 0' },
  closeBtn:    { background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer', flexShrink: 0, padding: 4 },
  body:        { padding: '20px 24px' },
  desc:        { fontSize: 14, color: '#475569', lineHeight: 1.7, margin: '0 0 20px' },
  descEmpty:   { fontSize: 14, color: '#94a3b8', fontStyle: 'italic', margin: '0 0 20px' },
  statsGrid:   { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 },
  statCard:    { background: '#f8fafc', borderRadius: 10, padding: '12px 14px' },
  statLabel:   { fontSize: 11, color: '#94a3b8', marginBottom: 4 },
  statValue:   { fontSize: 15, fontWeight: 700, color: '#0f172a' },
  section:     { marginBottom: 20 },
  sectionLabel:{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 },
  avatar:      { width: 28, height: 28, borderRadius: '50%', background: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 },
  taskRow:     { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#f8fafc', borderRadius: 8 },
  miniAttach:  { fontSize: 11, color: '#d97706', fontWeight: 600 },
  fileRow:     { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#f8fafc', borderRadius: 8, textDecoration: 'none' },
  noteItem:    { borderLeft: '2px solid #2563eb', paddingLeft: 10, marginBottom: 10 },
  noteText:    { fontSize: 13, color: '#1e293b', margin: 0 },
  noteMeta:    { fontSize: 11, color: '#94a3b8' },
  footer:      { padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 },
  editBtn:     { padding: '9px 18px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  notesBtn:    { padding: '9px 18px', border: 'none', borderRadius: 8, background: '#2563eb', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};